import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

type WithChildren = {
  children: ReactNode;
  className?: string;
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function useModalFocus<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const panelRef = useRef<T | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => {
      const preferred = panelRef.current?.querySelector<HTMLElement>('[data-dialog-close]');
      (preferred ?? panelRef.current)?.focus();
    });
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleEscape);
      window.requestAnimationFrame(() => previousFocusRef.current?.focus());
    };
  }, [open]);

  const trapFocus = (event: ReactKeyboardEvent<T>) => {
    if (event.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter(element => !element.hasAttribute('disabled'));
    if (focusable.length === 0) {
      event.preventDefault();
      panelRef.current.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return { panelRef, trapFocus };
}

export function CivicMapSurface({ children, className }: WithChildren) {
  return (
    <div
      className={cn(
        'relative isolate h-full min-h-0 overflow-hidden bg-[var(--c-bg)] text-[var(--c-text-primary)]',
        className
      )}
    >
      {children}
    </div>
  );
}

export function CivicMapPanel({ children, className }: WithChildren) {
  return (
    <div
      className={cn(
        'border border-[var(--c-border)] bg-[rgba(var(--c-surface-rgb),0.94)] text-[var(--c-text-primary)] shadow-sm backdrop-blur-md',
        className
      )}
    >
      {children}
    </div>
  );
}

type CivicMapChipProps = {
  label: string;
  active?: boolean;
  count?: number | string;
  muted?: boolean;
  icon?: LucideIcon;
  onClick?: () => void;
  className?: string;
};

export function CivicMapChip({
  label,
  active = false,
  count,
  muted = false,
  icon: Icon,
  onClick,
  className,
}: CivicMapChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-11 shrink-0 select-none items-center gap-2 whitespace-nowrap border px-3 font-[var(--pr-grotesk)] text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]',
        active
          ? 'border-[var(--c-text-primary)] bg-[var(--c-text-primary)] text-[var(--c-bg)]'
          : 'border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] hover:border-[var(--c-text-tertiary)] hover:text-[var(--c-text-primary)]',
        muted && !active && 'opacity-70',
        className
      )}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" strokeWidth={1.7} aria-hidden /> : null}
      <span>{label}</span>
      {count !== undefined ? (
        <span className={cn('font-[var(--pr-mono)] text-[10px]', active ? 'opacity-70' : 'text-[var(--c-text-tertiary)]')}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

type CivicMapButtonProps = {
  label: string;
  icon: LucideIcon;
  active?: boolean;
  compact?: boolean;
  badge?: number | string;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  dialogTrigger?: string;
};

export function CivicMapButton({
  label,
  icon: Icon,
  active = false,
  compact = false,
  badge,
  disabled = false,
  onClick,
  className,
  dialogTrigger,
}: CivicMapButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'relative inline-flex h-11 items-center justify-center gap-2 border px-3 font-[var(--pr-grotesk)] text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)] disabled:cursor-not-allowed disabled:opacity-50',
        active
          ? 'border-[var(--c-text-primary)] bg-[var(--c-text-primary)] text-[var(--c-bg)]'
          : 'border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-primary)] hover:border-[var(--c-text-tertiary)]',
        compact && 'w-11 px-0',
        className
      )}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      data-dialog-trigger={dialogTrigger}
    >
      <Icon className="h-4 w-4" strokeWidth={1.7} aria-hidden />
      {!compact ? <span>{label}</span> : null}
      {compact && badge !== undefined ? (
        <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full border-2 border-[var(--c-surface)] bg-[var(--c-accent)] px-1 font-[var(--pr-mono)] text-[9px] leading-none text-white">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

type CivicMapDrawerProps = {
  open: boolean;
  title: string;
  eyebrow?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

export function CivicMapDrawer({
  open,
  title,
  eyebrow,
  onClose,
  children,
  footer,
  className,
}: CivicMapDrawerProps) {
  const { panelRef, trapFocus } = useModalFocus<HTMLElement>(open, onClose);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            type="button"
            aria-label="Close drawer"
            className="fixed inset-0 z-[3400] bg-black/10 lg:absolute lg:bg-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            onKeyDown={trapFocus}
            className={cn(
              'fixed bottom-0 left-0 right-0 z-[3500] flex max-h-[78dvh] flex-col overflow-hidden border-t border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-primary)] shadow-2xl lg:absolute lg:bottom-4 lg:left-auto lg:right-4 lg:top-4 lg:w-[390px] lg:max-h-none lg:border lg:shadow-sm',
              className
            )}
            style={{ backgroundColor: 'var(--c-surface)' }}
            initial={{ y: 32 }}
            animate={{ y: 0 }}
            exit={{ y: 32 }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
          >
            <div className="flex shrink-0 items-start gap-4 border-b border-[var(--c-border)] px-5 py-4">
              <div className="min-w-0 flex-1">
                {eyebrow ? (
                  <p className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.22em] text-[var(--c-accent)]">
                    {eyebrow}
                  </p>
                ) : null}
                <h2 className="mt-1 font-['Newsreader'] text-2xl leading-none text-[var(--c-text-primary)]">
                  {title}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-dialog-close
                className="flex h-11 w-11 items-center justify-center border border-[var(--c-border)] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
                aria-label="Close drawer"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {children}
            </div>
            {footer ? <div className="shrink-0 border-t border-[var(--c-border)] px-5 py-4">{footer}</div> : null}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

type CivicMapBottomSheetProps = {
  open: boolean;
  title: string;
  eyebrow?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

export function CivicMapBottomSheet({
  open,
  title,
  eyebrow,
  onClose,
  children,
  footer,
  className,
}: CivicMapBottomSheetProps) {
  const { panelRef, trapFocus } = useModalFocus<HTMLElement>(open, onClose);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            type="button"
            aria-label="Close details"
            className="fixed inset-0 z-[3200] bg-black/10 md:absolute md:bg-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.section
          ref={panelRef}
          className={cn(
            'fixed inset-x-2 bottom-2 z-[3300] flex max-h-[62dvh] flex-col overflow-hidden border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-primary)] shadow-2xl md:inset-x-auto md:bottom-5 md:right-5 md:w-[430px] md:shadow-sm',
            className
          )}
          style={{ backgroundColor: 'var(--c-surface)' }}
          initial={{ y: 48 }}
          animate={{ y: 0 }}
          exit={{ y: 48 }}
          transition={{ type: 'spring', damping: 30, stiffness: 260 }}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          onKeyDown={trapFocus}
        >
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-[var(--c-border)] md:hidden" />
          <div className="flex shrink-0 items-start gap-4 border-b border-[var(--c-border)] px-4 py-3 md:px-5">
            <div className="min-w-0 flex-1">
              {eyebrow ? (
                <p className="font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--c-accent)]">
                  {eyebrow}
                </p>
              ) : null}
              <h2 className="mt-1 line-clamp-2 font-['Newsreader'] text-2xl leading-tight text-[var(--c-text-primary)]">
                {title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-dialog-close
              className="flex h-11 w-11 shrink-0 items-center justify-center border border-[var(--c-border)] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-accent)]"
              aria-label="Close details"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-5">
            {children}
          </div>
          {footer ? <div className="shrink-0 border-t border-[var(--c-border)] px-4 py-3 md:px-5">{footer}</div> : null}
          </motion.section>
        </>
      ) : null}
    </AnimatePresence>
  );
}

export function CivicSectionLabel({ children, className }: WithChildren) {
  return (
    <div className={cn('flex items-center gap-3 font-[var(--pr-mono)] text-[10px] uppercase tracking-[0.22em] text-[var(--c-text-tertiary)]', className)}>
      <span className="h-[3px] w-4 bg-[var(--c-accent)]" aria-hidden />
      <span>{children}</span>
      <span className="h-px min-w-6 flex-1 bg-[var(--c-border)]" aria-hidden />
    </div>
  );
}
